import React, { useState, useEffect } from 'react';
import { Header, Button, Modal, Card, LoadingSpinner, DataGrid } from '../layout';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { setDocumentTitle } from '../utils/titleUtils';
import {
    FaEye,
    FaFileInvoice,
    FaTruck,
    FaBan,
    FaCheckCircle,
    FaClock,
    FaFileAlt,
    FaUser,
    FaCalendarAlt,
    FaShoppingCart,
    FaClipboardList,
    FaFilter,
    FaChevronDown,
    FaChevronUp,
    FaTimes,
    FaSearch,
    FaBoxOpen,
    FaAngleRight
} from 'react-icons/fa';

/* ─────────────────────────────────────────────
   STATUS CONFIG
───────────────────────────────────────────── */
const STATUS_CONFIG = {
    annullato: { label: 'Annullato', color: 'red', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-500', icon: FaBan },
    fatturato: { label: 'Fatturato', color: 'emerald', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: FaFileInvoice },
    spedito: { label: 'Spedito', color: 'blue', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', icon: FaTruck },
    confermato: { label: 'Confermato', color: 'amber', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: FaCheckCircle },
    in_elaborazione: { label: 'In Elaborazione', color: 'gray', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400', icon: FaClock },
};

// Quote-specific filter values map to colors. Keys are the literal StatoOfferta strings.
const QUOTE_STATUS_CONFIG = {
    'In trattativa':                 { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500' },
    'Chiusa Vinta':                  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    'Chiusa Persa':                  { bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200',     dot: 'bg-red-500' },
    'Sospesa':                       { bg: 'bg-gray-100',   text: 'text-gray-600',    border: 'border-gray-200',    dot: 'bg-gray-400' },
    'In Attesa Offerte Fornitori':   { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500' },
};
const QUOTE_STATUS_KEYS = Object.keys(QUOTE_STATUS_CONFIG);

const QuoteStatusPill = ({ status, small = false }) => {
    const cfg = QUOTE_STATUS_CONFIG[status] || { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' };
    const label = status || '—';
    return (
        <span className={`inline-flex items-center gap-1.5 font-semibold rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border} ${small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
            {label}
        </span>
    );
};

const StatusPill = ({ statusKey, small = false }) => {
    const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.in_elaborazione;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 font-semibold rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border} ${small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const getOrderStatusKey = (order) => {
    if (order.Cancelled === '1') return 'annullato';
    if (order.Invoiced === '1') return 'fatturato';
    if (order.Delivered === '1') return 'spedito';
    const isConfirmed = order.Cancelled === '0' && order.Invoiced === '0' && order.Delivered === '0'
        && order.ConfirmedDeliveryDate && !order.ConfirmedDeliveryDate.startsWith('1799-12-31');
    return isConfirmed ? 'confermato' : 'in_elaborazione';
};

const getQuoteStatusKey = (quote) => {
    const s = (quote.StatoOfferta || '').toLowerCase();
    if (s.includes('accettata') || s.includes('accepted')) return 'fatturato';
    if (s.includes('rifiutata') || s.includes('rejected')) return 'annullato';
    if (s.includes('attesa') || s.includes('pending')) return 'confermato';
    return 'in_elaborazione';
};

const fmt = (v) => {
    if (!v && v !== 0) return '€ 0,00';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
};

const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/* ─────────────────────────────────────────────
   MOBILE ORDER CARD
───────────────────────────────────────────── */
const OrderCard = ({ order, onView }) => {
    const sk = getOrderStatusKey(order);
    const cfg = STATUS_CONFIG[sk];
    const Icon = cfg.icon;
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform">
            {/* Top stripe */}
            <div className={`h-1 w-full ${cfg.dot}`} />
            <div className="p-4">
                {/* Row 1 */}
                <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                        <p className="font-mono font-bold text-gray-900 text-base leading-tight">
                            {order.InternalOrdNo || order.ExternalOrdNo}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(order.OrderDate)}</p>
                    </div>
                    <StatusPill statusKey={sk} />
                </div>
                {/* Row 2 - Customer */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-gray-400 text-[10px]" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{order.CustomerName}</p>
                        <p className="text-xs text-gray-400">{order.SalespersonName || order.Salesperson || '—'}</p>
                    </div>
                </div>
                {/* Row 3 - Amount + Action */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Totale</p>
                        <p className="text-base font-bold text-gray-900 tabular-nums">{fmt(order.PayableAmount)}</p>
                    </div>
                    <button
                        onClick={() => onView(order.SaleOrdId)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold shadow-sm hover:bg-red-600 active:bg-red-700 transition-colors"
                    >
                        <FaEye className="text-xs" />
                        Dettagli
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   MOBILE QUOTE CARD
───────────────────────────────────────────── */
const QuoteCard = ({ quote, onView }) => {
    const cfg = QUOTE_STATUS_CONFIG[quote.StatoOfferta] || { dot: 'bg-gray-400' };
    const reqNo = parseInt(quote.RequestNo) || 1;
    const avgAmount = reqNo > 1 ? quote.TotalAmount / reqNo : null;
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform">
            <div className={`h-1 w-full ${cfg.dot}`} />
            <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                        <p className="font-mono font-bold text-gray-900 text-base leading-tight">{quote.QuotationNo}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(quote.QuotationDate)}</p>
                    </div>
                    <QuoteStatusPill status={quote.StatoOfferta} />
                </div>
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-gray-400 text-[10px]" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{quote.CompanyName}</p>
                        <p className="text-xs text-gray-400">{quote.SalespersonName || quote.Salesperson || '—'}</p>
                    </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Totale</p>
                        {avgAmount !== null ? (
                            <>
                                <p className="text-base font-bold text-gray-900 tabular-nums">{fmt(avgAmount)}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">{reqNo} rich. · totale {fmt(quote.TotalAmount)}</p>
                            </>
                        ) : (
                            <p className="text-base font-bold text-gray-900 tabular-nums">{fmt(quote.TotalAmount)}</p>
                        )}
                    </div>
                    <button
                        onClick={() => onView(quote.CustQuotaId)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold shadow-sm hover:bg-red-600 active:bg-red-700 transition-colors"
                    >
                        <FaEye className="text-xs" />
                        Dettagli
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   STAT CARD
───────────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
            <Icon className="text-base" />
        </div>
        <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
            <p className="text-[11px] text-gray-400 uppercase tracking-wider mt-0.5">{label}</p>
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   FILTER DRAWER (mobile) / PANEL (desktop)
───────────────────────────────────────────── */
const FilterPanel = ({
    dateFrom, setDateFrom, dateTo, setDateTo,
    managedAgents, selectedAgent, setSelectedAgent,
    onSearch, isLoading, isMobile, isOpen, onClose,
    statusFilter, setStatusFilter, orders,
    activeTab = 'orders', quotes = []
}) => {
    const isQuotes = activeTab === 'quotes';
    const statusFilters = isQuotes
        ? [{ key: 'all', label: 'Tutti' }, ...QUOTE_STATUS_KEYS.map(k => ({ key: k, label: k }))]
        : [
            { key: 'all', label: 'Tutti' },
            { key: 'in_elaborazione', label: 'In Elaborazione' },
            { key: 'confermato', label: 'Confermato' },
            { key: 'spedito', label: 'Spedito' },
            { key: 'fatturato', label: 'Fatturato' },
            { key: 'annullato', label: 'Annullato' },
        ];

    const getCount = (key) => {
        if (key === 'all') return isQuotes ? quotes.length : orders.length;
        if (isQuotes) return quotes.filter(q => (q.StatoOfferta || '') === key).length;
        return orders.filter(o => getOrderStatusKey(o) === key).length;
    };

    const inner = (
        <div className="flex flex-col gap-5">
            {/* Date range */}
            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaCalendarAlt /> Periodo
                </label>
                <div className=" gap-2">
                    <div className="">
                        <p className="text-[10px] text-gray-400 mb-1">Dal</p>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition"
                        />
                    </div>
                    <div className="">
                        <p className="text-[10px] text-gray-400 mb-1">Al</p>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition"
                        />
                    </div>
                </div>
            </div>

            {/* Agent filter */}
            {managedAgents.length > 0 && (
                <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <FaUser /> Agente
                    </label>
                    <select
                        value={selectedAgent}
                        onChange={e => setSelectedAgent(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition appearance-none"
                    >
                        <option value="all">Tutti gli agenti</option>
                        {managedAgents.map(a => (
                            <option key={a.Salesperson} value={a.Salesperson}>{a.Name}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Status chips */}
            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaFilter /> {isQuotes ? 'Stato offerta' : 'Stato ordine'}
                </label>
                <div className="flex flex-wrap gap-2">
                    {statusFilters.map(f => {
                        const active = statusFilter === f.key;
                        const cfg = isQuotes ? QUOTE_STATUS_CONFIG[f.key] : STATUS_CONFIG[f.key];
                        const count = getCount(f.key);
                        return (
                            <button
                                key={f.key}
                                onClick={() => setStatusFilter(f.key)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${active
                                    ? (cfg ? `${cfg.bg} ${cfg.text} ${cfg.border}` : 'bg-gray-200 text-gray-700 border-gray-300')
                                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                {f.key !== 'all' && cfg && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />}
                                {f.label}
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/60' : 'bg-gray-100'}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Search button */}
            <button
                onClick={() => { onSearch(); isMobile && onClose(); }}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-red-500 text-white rounded-xl font-semibold text-sm hover:bg-red-600 active:bg-red-700 transition-colors disabled:opacity-50 shadow-sm shadow-red-200"
            >
                {isLoading ? (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                ) : <FaSearch className="text-xs" />}
                {isLoading ? 'Caricamento...' : 'Cerca'}
            </button>
        </div>
    );

    if (!isMobile) {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-4">
                <p className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaFilter className="text-red-400" /> Filtri
                </p>
                {inner}
            </div>
        );
    }

    // Mobile drawer
    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />
            {/* Sheet */}
            <div className={`fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
                {/* Handle */}
                <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 rounded-full bg-gray-200" />
                </div>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <p className="font-bold text-gray-900 flex items-center gap-2"><FaFilter className="text-red-400" /> Filtri</p>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">
                        <FaTimes className="text-sm" />
                    </button>
                </div>
                <div className="px-5 py-5 overflow-y-auto max-h-[75vh]">
                    {inner}
                </div>
            </div>
        </>
    );
};

/* ─────────────────────────────────────────────
   DETAIL MODAL
───────────────────────────────────────────── */
const DetailModalContent = ({ detailsLoading, selectedItem, itemDetails, activeTab, closeModal }) => {
    if (detailsLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-10 h-10 border-3 border-gray-200 border-t-red-500 rounded-full animate-spin" />
                <p className="text-sm text-gray-400">Caricamento dettagli...</p>
            </div>
        );
    }
    if (!selectedItem) {
        return <div className="py-16 text-center text-gray-400 text-sm">Nessun dato disponibile</div>;
    }

    const isOrder = activeTab === 'orders';
    const docNo = isOrder ? (selectedItem.InternalOrdNo || selectedItem.ExternalOrdNo) : selectedItem.QuotationNo;
    const docDate = fmtDate(isOrder ? selectedItem.OrderDate : selectedItem.QuotationDate);
    const sk = isOrder ? getOrderStatusKey(selectedItem) : getQuoteStatusKey(selectedItem);
    const total = fmt(isOrder ? selectedItem.PayableAmount : selectedItem.TotalAmount);
    const quoteReqNo = !isOrder ? (parseInt(selectedItem.RequestNo) || 1) : 1;
    const quoteAvgAmount = !isOrder && quoteReqNo > 1 ? selectedItem.TotalAmount / quoteReqNo : null;

    return (
        <div className="-mx-5 -my-4 sm:-mx-6">
            {/* Header */}
            <div className="px-5 sm:px-8 pt-5 pb-4 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-0.5">{isOrder ? 'Ordine' : 'Offerta'}</p>
                        <p className="text-xl font-bold text-gray-900 font-mono">{docNo}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{docDate}</p>
                    </div>
                    <StatusPill statusKey={sk} />
                </div>
            </div>

            {/* Meta grid — scrollable on mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
                {[
                    { label: 'Cliente', value: selectedItem.CustomerName || selectedItem.CompanyName },
                    { label: 'Codice', value: isOrder ? selectedItem.Customer : selectedItem.CustCont, mono: true },
                    { label: 'Agente', value: selectedItem.SalespersonName || selectedItem.Salesperson },
                    { label: 'Consegna', value: isOrder && selectedItem.ConfirmedDeliveryDate ? fmtDate(selectedItem.ConfirmedDeliveryDate) : null },
                ].map((f, i) => (
                    <div key={i} className={`px-4 sm:px-6 py-3 ${i >= 2 ? 'border-t border-gray-100 sm:border-t-0' : ''}`}>
                        <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">{f.label}</p>
                        <p className={`text-sm font-semibold text-gray-900 ${f.mono ? 'font-mono' : ''}`}>
                            {f.value || <span className="text-gray-300 font-normal">—</span>}
                        </p>
                    </div>
                ))}
            </div>

            {/* Items */}
            <div className="px-5 sm:px-8 pt-4 pb-2">
                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">
                    Righe — {itemDetails?.length || 0} articoli
                </p>
            </div>

            {!itemDetails || itemDetails.length === 0 ? (
                <div className="mx-5 sm:mx-8 mb-4 py-8 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-sm text-gray-400">Nessuna riga disponibile</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[540px]">
                        <thead>
                            <tr className="bg-gray-50 border-y border-gray-100">
                                <th className="pl-5 sm:pl-8 pr-3 py-2.5 text-center text-[9px] font-bold tracking-widest uppercase text-gray-400">Pos.</th>
                                <th className="px-3 py-2.5 text-left text-[9px] font-bold tracking-widest uppercase text-gray-400">Codice</th>
                                <th className="px-3 py-2.5 text-left text-[9px] font-bold tracking-widest uppercase text-gray-400">Descrizione</th>
                                <th className="px-3 py-2.5 text-right text-[9px] font-bold tracking-widest uppercase text-gray-400">Qtà</th>
                                <th className="px-3 py-2.5 text-right text-[9px] font-bold tracking-widest uppercase text-gray-400">Prezzo</th>
                                <th className="px-3 py-2.5 text-right text-[9px] font-bold tracking-widest uppercase text-gray-400">Sconto</th>
                                <th className="pl-3 pr-5 sm:pr-8 py-2.5 text-right text-[9px] font-bold tracking-widest uppercase text-gray-400">Totale</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itemDetails.map((item, i) => {
                                const prevPos = i > 0 ? itemDetails[i - 1].Posizione : null;
                                const isNewPos = item.Posizione && item.Posizione !== prevPos;
                                return (
                                    <tr key={item.Id || i} className={`border-t ${isNewPos ? 'border-gray-300' : 'border-gray-50'} ${i % 2 === 0 ? '' : 'bg-gray-50/40'} hover:bg-red-50/30 transition-colors`}>
                                        <td className="pl-5 sm:pl-8 pr-3 py-3 text-center text-[10px] font-semibold text-gray-400">{item.Posizione || '—'}</td>
                                        <td className="px-3 py-3 font-mono text-xs text-gray-500">{item.Item}</td>
                                        <td className="px-3 py-3 text-gray-800 max-w-[160px]"><span className="line-clamp-2 text-xs">{item.Description}</span></td>
                                        <td className="px-3 py-3 text-right text-xs text-gray-600 tabular-nums whitespace-nowrap">{item.Qty} {item.UoM}</td>
                                        <td className="px-3 py-3 text-right text-xs text-gray-600 tabular-nums whitespace-nowrap">{fmt(item.NetPrice || item.UnitValue)}</td>
                                        <td className="px-3 py-3 text-right text-xs text-gray-400 whitespace-nowrap">{item.DiscountFormula || '—'}</td>
                                        <td className="pl-3 pr-5 sm:pr-8 py-3 text-right text-xs font-bold text-gray-900 tabular-nums whitespace-nowrap">{fmt(item.TaxableAmount)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Totals */}
            <div className="px-5 sm:px-8 py-5 border-t border-gray-100">
                <div className="flex items-end justify-between gap-4">
                    <button onClick={closeModal} className="text-sm text-gray-400 hover:text-gray-700 underline underline-offset-2 transition-colors">
                        Chiudi
                    </button>
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-8 pt-2 border-t border-gray-200">
                            <span className="text-sm font-bold text-gray-900">Totale (esclusa IVA)</span>
                            <span className="text-lg font-black text-gray-900 tabular-nums">{fmt(selectedItem.TaxableAmount)}</span>
                        </div>
                        {!isOrder && quoteReqNo > 1 && (
                            <>
                                <div className="flex items-center justify-between gap-8 pt-2 border-t border-gray-200 mt-2">
                                    <span className="text-sm font-bold text-gray-900">N. Richieste</span>
                                    <span className="text-lg font-black text-gray-900 tabular-nums">{quoteReqNo}</span>
                                </div>
                                <div className="flex items-center justify-between gap-8 pt-2 border-t border-gray-200">
                                    <span className="text-sm font-bold text-gray-900">Totale medio</span>
                                    <span className="text-lg font-black text-gray-900 tabular-nums">{fmt(quoteAvgAmount)}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   DESKTOP TABLE COLUMNS
───────────────────────────────────────────── */
const DETAIL_BUTTON_CLASS = 'inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-all shadow-sm';

const toTimestamp = (d) => (d ? new Date(d).getTime() : null);

const getQuoteAmount = (quote) => {
    const reqNo = parseInt(quote.RequestNo) || 1;
    return reqNo > 1 ? quote.TotalAmount / reqNo : quote.TotalAmount;
};

const getOrderColumns = ({ onView }) => [
    {
        key: 'InternalOrdNo',
        header: 'N. Ordine',
        headerClassName: 'pl-6 pr-4',
        cellClassName: 'pl-6 pr-4',
        sortValue: (o) => o.InternalOrdNo || o.ExternalOrdNo,
        render: (o) => <span className="font-mono font-bold text-gray-900 text-sm">{o.InternalOrdNo || o.ExternalOrdNo}</span>
    },
    {
        key: 'OrderDate',
        header: 'Data',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (o) => toTimestamp(o.OrderDate),
        render: (o) => fmtDate(o.OrderDate)
    },
    {
        key: 'ExternalOrdNo',
        header: 'Rif. Esterno',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (o) => o.ExternalOrdNo,
        render: (o) => o.ExternalOrdNo || '—'
    },
    {
        key: 'Customer',
        header: 'Cliente',
        sortValue: (o) => o.CustomerName,
        render: (o) => (
            <>
                <p className="text-sm font-semibold text-gray-800">{o.CustomerName}</p>
                <p className="text-xs text-gray-400">{o.Customer}</p>
            </>
        )
    },
    {
        key: 'Salesperson',
        header: 'Agente',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (o) => o.SalespersonName || o.Salesperson,
        render: (o) => o.SalespersonName || o.Salesperson || '—'
    },
    {
        key: 'status',
        header: 'Stato',
        sortValue: (o) => STATUS_CONFIG[getOrderStatusKey(o)].label,
        render: (o) => <StatusPill statusKey={getOrderStatusKey(o)} />
    },
    {
        key: 'PayableAmount',
        header: 'Totale',
        align: 'right',
        cellClassName: 'px-4 font-bold text-gray-900 text-sm tabular-nums',
        sortValue: (o) => Number(o.PayableAmount) || 0,
        render: (o) => fmt(o.PayableAmount)
    },
    {
        key: 'actions',
        header: '',
        align: 'center',
        headerClassName: 'pl-4 pr-6',
        cellClassName: 'pl-4 pr-6',
        render: (o) => (
            <button onClick={() => onView(o.SaleOrdId)} className={DETAIL_BUTTON_CLASS}>
                <FaEye /> Dettagli
            </button>
        )
    }
];

const getQuoteColumns = ({ onView }) => [
    {
        key: 'QuotationNo',
        header: 'N. Offerta',
        headerClassName: 'pl-6 pr-4',
        cellClassName: 'pl-6 pr-4',
        sortValue: (q) => q.QuotationNo,
        render: (q) => <span className="font-mono font-bold text-gray-900 text-sm">{q.QuotationNo}</span>
    },
    {
        key: 'QuotationDate',
        header: 'Data',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (q) => toTimestamp(q.QuotationDate),
        render: (q) => fmtDate(q.QuotationDate)
    },
    {
        key: 'ExternalOrdNo',
        header: 'Rif. Esterno',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (q) => q.ExternalOrdNo,
        render: (q) => q.ExternalOrdNo || '—'
    },
    {
        key: 'CompanyName',
        header: 'Cliente',
        sortValue: (q) => q.CompanyName,
        render: (q) => (
            <>
                <p className="text-sm font-semibold text-gray-800">{q.CompanyName}</p>
                <p className="text-xs text-gray-400">{q.CustCont}</p>
            </>
        )
    },
    {
        key: 'Salesperson',
        header: 'Agente',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (q) => q.SalespersonName || q.Salesperson,
        render: (q) => q.SalespersonName || q.Salesperson || '—'
    },
    {
        key: 'StatoOfferta',
        header: 'Stato',
        sortValue: (q) => q.StatoOfferta,
        render: (q) => <QuoteStatusPill status={q.StatoOfferta} />
    },
    {
        key: 'RequestNo',
        header: 'N. Rich.',
        align: 'center',
        cellClassName: 'px-4 text-sm font-semibold text-gray-700 tabular-nums',
        sortValue: (q) => parseInt(q.RequestNo) || 1,
        render: (q) => {
            const reqNo = parseInt(q.RequestNo) || 1;
            return reqNo > 1 ? reqNo : '—';
        }
    },
    {
        key: 'TotalAmount',
        header: 'Totale',
        align: 'right',
        cellClassName: 'px-4 font-bold text-gray-900 text-sm tabular-nums',
        sortValue: (q) => Number(getQuoteAmount(q)) || 0,
        render: (q) => fmt(getQuoteAmount(q))
    },
    {
        key: 'actions',
        header: '',
        align: 'center',
        headerClassName: 'pl-4 pr-6',
        cellClassName: 'pl-4 pr-6',
        render: (q) => (
            <button onClick={() => onView(q.CustQuotaId)} className={DETAIL_BUTTON_CLASS}>
                <FaEye /> Dettagli
            </button>
        )
    }
];

const ORDER_SEARCH_KEYS = ['InternalOrdNo', 'ExternalOrdNo', 'CustomerName', 'Customer', 'SalespersonName', 'Salesperson'];
const QUOTE_SEARCH_KEYS = ['QuotationNo', 'ExternalOrdNo', 'CompanyName', 'CustCont', 'SalespersonName', 'Salesperson'];
const ORDER_INITIAL_SORT = { key: 'OrderDate', dir: 'desc' };
const QUOTE_INITIAL_SORT = { key: 'QuotationDate', dir: 'desc' };

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
const OrdersStatus = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [mobileSearch, setMobileSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('orders');
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemDetails, setItemDetails] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date(); d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

    const [managedAgents, setManagedAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('all');

    const filteredOrders = statusFilter === 'all'
        ? orders
        : orders.filter(o => getOrderStatusKey(o) === statusFilter);

    const filteredQuotes = statusFilter === 'all'
        ? quotes
        : quotes.filter(q => (q.StatoOfferta || '') === statusFilter);

    // Reset status filter when switching between Ordini and Offerte (keys differ)
    useEffect(() => { setStatusFilter('all'); }, [activeTab]);

    useEffect(() => { setDocumentTitle('Stato Ordini e Offerte Mago'); }, []);
    useEffect(() => { if (user) fetchData(); }, [user]); // eslint-disable-line

    const handleFilterChange = setStatusFilter;

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let customerCode = null, salesperson = null;
            const isAdmin = user?.role === 'admin';
            if (!isAdmin) {
                if (user.userType === 'client') {
                    customerCode = user.client_data?.CustSupp || user.id;
                } else if (user.userType === 'agent') {
                    salesperson = user.agent_data?.Salesperson || user.id;
                    customerCode = user.id;
                } else {
                    customerCode = user.CustSupp || user.id;
                    salesperson = user.Salesperson || null;
                }
            }
            const response = await api.getOrdersStatus(
                customerCode, salesperson, dateFrom, dateTo,
                selectedAgent !== 'all' ? selectedAgent : null, isAdmin
            );
            if (response.status === 'success') {
                setOrders(response.orders || []);
                setQuotes(response.quotes || []);
                if (response.managed_agents?.length > 0) setManagedAgents(response.managed_agents);
            } else throw new Error('Invalid response');
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile caricare i dati' });
        } finally {
            setIsLoading(false);
        }
    };

    const viewOrderDetails = async (id) => {
        setDetailsLoading(true); setModalOpen(true); setItemDetails(null);
        try {
            const r = await api.getOrderDetails(id);
            if (r.status === 'success') { setSelectedItem(r.order); setItemDetails(r.items || []); }
        } catch { Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile caricare i dettagli' }); setModalOpen(false); }
        finally { setDetailsLoading(false); }
    };

    const viewQuoteDetails = async (id) => {
        setDetailsLoading(true); setModalOpen(true); setItemDetails(null);
        try {
            const r = await api.getQuoteDetails(id);
            if (r.status === 'success') { setSelectedItem(r.quote); setItemDetails(r.items || []); }
        } catch { Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile caricare i dettagli' }); setModalOpen(false); }
        finally { setDetailsLoading(false); }
    };

    const closeModal = () => { setModalOpen(false); setSelectedItem(null); setItemDetails(null); };

    if (isLoading && orders.length === 0 && quotes.length === 0) {
        return (
            <>
                <Header />
                <div className="flex flex-col justify-center items-center min-h-[60vh] gap-3">
                    <div className="w-10 h-10 border-[3px] border-gray-200 border-t-red-500 rounded-full animate-spin" />
                    <p className="text-sm text-gray-400">Caricamento dati...</p>
                </div>
            </>
        );
    }

    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50/50">
                <div className="w-full px-3 sm:px-5 lg:px-8 py-6 sm:py-8">

                    {/* ── Page Header ── */}
                    <div className="mb-6 sm:mb-8">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-100 flex-shrink-0">
                                <FaClipboardList className="text-white text-lg sm:text-xl" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-gray-900 leading-tight">
                                    Stato Ordini e Offerte
                                </h1>
                                <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                                    {user?.role === 'funzionario' || user?.role === 'admin'
                                        ? 'Panoramica ordini e offerte della tua area'
                                        : 'I tuoi ordini e le tue offerte'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ── Layout: sidebar (desktop) + content ── */}
                    <div className="flex gap-6">

                        {/* ─ Desktop Sidebar ─ */}
                        <div className="hidden lg:block w-72 flex-shrink-0">
                            <FilterPanel
                                dateFrom={dateFrom} setDateFrom={setDateFrom}
                                dateTo={dateTo} setDateTo={setDateTo}
                                managedAgents={managedAgents}
                                selectedAgent={selectedAgent} setSelectedAgent={setSelectedAgent}
                                onSearch={fetchData} isLoading={isLoading}
                                isMobile={false} isOpen={false} onClose={() => { }}
                                statusFilter={statusFilter} setStatusFilter={handleFilterChange}
                                orders={orders}
                                quotes={quotes}
                                activeTab={activeTab}
                            />
                        </div>

                        {/* ─ Main Content ─ */}
                        <div className="flex-1 min-w-0">

                            {/* Mobile: Top bar with tabs + filter button */}
                            <div className="flex items-center justify-between gap-3 mb-4 lg:hidden">
                                {/* Tabs */}
                                <div className="flex-1 flex bg-white rounded-xl border border-gray-100 shadow-sm p-1 gap-1">
                                    {[
                                        { key: 'orders', label: 'Ordini', icon: FaShoppingCart, count: orders.length },
                                        { key: 'quotes', label: 'Offerte', icon: FaFileAlt, count: quotes.length },
                                    ].map(tab => (
                                        <button
                                            key={tab.key}
                                            onClick={() => setActiveTab(tab.key)}
                                            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.key
                                                ? 'bg-red-500 text-white shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                        >
                                            <tab.icon className="text-xs" />
                                            {tab.label}
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/25' : 'bg-gray-100'}`}>{tab.count}</span>
                                        </button>
                                    ))}
                                </div>
                                {/* Filter trigger */}
                                <button
                                    onClick={() => setFilterDrawerOpen(true)}
                                    className="flex-shrink-0 h-10 px-4 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 flex items-center gap-2 shadow-sm hover:border-red-300 hover:text-red-600 transition-all"
                                >
                                    <FaFilter className="text-xs" />
                                    Filtri
                                    {statusFilter !== 'all' && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                </button>
                            </div>

                            {/* Desktop Tabs */}
                            <div className="hidden lg:flex items-center gap-2 mb-5">
                                {[
                                    { key: 'orders', label: 'Ordini', icon: FaShoppingCart, count: orders.length },
                                    { key: 'quotes', label: 'Offerte', icon: FaFileAlt, count: quotes.length },
                                ].map(tab => (
                                    <button
                                        key={tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all border ${activeTab === tab.key
                                            ? 'bg-white border-gray-200 text-gray-900 shadow-sm'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/60'
                                            }`}
                                    >
                                        <tab.icon className="text-xs" />
                                        {tab.label}
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-gray-100' : 'bg-gray-100'}`}>{tab.count}</span>
                                    </button>
                                ))}
                            </div>

                            {/* ─ Orders Tab ─ */}
                            {activeTab === 'orders' && (
                                <>
                                    {/* Mobile Search */}
                                    <div className="lg:hidden mb-3">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Cerca ordine, cliente..."
                                                value={mobileSearch}
                                                onChange={(e) => setMobileSearch(e.target.value)}
                                                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-white"
                                            />
                                            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            {mobileSearch && (
                                                <button onClick={() => setMobileSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">&#10005;</button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Mobile Cards */}
                                    <div className="lg:hidden space-y-3">
                                        {filteredOrders.length === 0 ? (
                                            <div className="bg-white rounded-2xl border border-gray-100 p-10 flex flex-col items-center gap-3 text-center">
                                                <FaBoxOpen className="text-3xl text-gray-300" />
                                                <p className="font-semibold text-gray-600">Nessun ordine trovato</p>
                                                <p className="text-sm text-gray-400">Prova a modificare i filtri</p>
                                                {statusFilter !== 'all' && (
                                                    <button onClick={() => handleFilterChange('all')} className="text-sm text-red-500 font-semibold hover:underline">Rimuovi filtro stato</button>
                                                )}
                                            </div>
                                        ) : (
                                            filteredOrders.filter(o => {
                                                if (!mobileSearch) return true;
                                                const s = mobileSearch.toLowerCase();
                                                return [o.InternalOrdNo, o.ExternalOrdNo, o.CustomerName, o.CompanyName, o.SalespersonName, o.Salesperson].some(v => v && String(v).toLowerCase().includes(s));
                                            }).map(o => <OrderCard key={o.SaleOrdId} order={o} onView={viewOrderDetails} />)
                                        )}
                                    </div>

                                    {/* Desktop Table */}
                                    <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                        {filteredOrders.length === 0 ? (
                                            <div className="py-16 flex flex-col items-center gap-3 text-center">
                                                <FaBoxOpen className="text-4xl text-gray-200" />
                                                <p className="font-semibold text-gray-500">Nessun ordine trovato</p>
                                                {statusFilter !== 'all' && (
                                                    <button onClick={() => handleFilterChange('all')} className="text-sm text-red-500 font-semibold hover:underline mt-1">Mostra tutti</button>
                                                )}
                                            </div>
                                        ) : (
                                            <DataGrid
                                                columns={getOrderColumns({ onView: viewOrderDetails })}
                                                rows={filteredOrders}
                                                rowKey="SaleOrdId"
                                                searchKeys={ORDER_SEARCH_KEYS}
                                                searchPlaceholder="Cerca ordine, cliente, agente..."
                                                initialSort={ORDER_INITIAL_SORT}
                                            />
                                        )}
                                    </div>
                                </>
                            )}

                            {/* ─ Quotes Tab ─ */}
                            {activeTab === 'quotes' && (
                                <>
                                    {/* Mobile Search */}
                                    <div className="lg:hidden mb-3">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Cerca offerta, cliente..."
                                                value={mobileSearch}
                                                onChange={(e) => setMobileSearch(e.target.value)}
                                                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-white"
                                            />
                                            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            {mobileSearch && (
                                                <button onClick={() => setMobileSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">&#10005;</button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Mobile Cards */}
                                    <div className="lg:hidden space-y-3">
                                        {filteredQuotes.length === 0 ? (
                                            <div className="bg-white rounded-2xl border border-gray-100 p-10 flex flex-col items-center gap-3 text-center">
                                                <FaBoxOpen className="text-3xl text-gray-300" />
                                                <p className="font-semibold text-gray-600">Nessuna offerta trovata</p>
                                                {statusFilter !== 'all' && (
                                                    <button onClick={() => handleFilterChange('all')} className="text-sm text-red-500 font-semibold hover:underline">Rimuovi filtro stato</button>
                                                )}
                                            </div>
                                        ) : (
                                            filteredQuotes.filter(q => {
                                                if (!mobileSearch) return true;
                                                const s = mobileSearch.toLowerCase();
                                                return [q.QuotationNo, q.CompanyName, q.CustomerName, q.SalespersonName, q.Salesperson].some(v => v && String(v).toLowerCase().includes(s));
                                            }).map(q => <QuoteCard key={q.CustQuotaId} quote={q} onView={viewQuoteDetails} />)
                                        )}
                                    </div>

                                    {/* Desktop Table */}
                                    <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                        {filteredQuotes.length === 0 ? (
                                            <div className="py-16 flex flex-col items-center gap-3">
                                                <FaBoxOpen className="text-4xl text-gray-200" />
                                                <p className="font-semibold text-gray-500">Nessuna offerta trovata</p>
                                                {statusFilter !== 'all' && (
                                                    <button onClick={() => handleFilterChange('all')} className="text-sm text-red-500 font-semibold hover:underline mt-1">Mostra tutte</button>
                                                )}
                                            </div>
                                        ) : (
                                            <DataGrid
                                                columns={getQuoteColumns({ onView: viewQuoteDetails })}
                                                rows={filteredQuotes}
                                                rowKey="CustQuotaId"
                                                searchKeys={QUOTE_SEARCH_KEYS}
                                                searchPlaceholder="Cerca offerta, cliente, agente..."
                                                initialSort={QUOTE_INITIAL_SORT}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Mobile Filter Drawer ── */}
            <FilterPanel
                dateFrom={dateFrom} setDateFrom={setDateFrom}
                dateTo={dateTo} setDateTo={setDateTo}
                managedAgents={managedAgents}
                selectedAgent={selectedAgent} setSelectedAgent={setSelectedAgent}
                onSearch={fetchData} isLoading={isLoading}
                isMobile={true} isOpen={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}
                statusFilter={statusFilter} setStatusFilter={handleFilterChange}
                orders={orders}
                quotes={quotes}
                activeTab={activeTab}
            />

            {/* ── Detail Modal ── */}
            <Modal
                isOpen={modalOpen}
                onClose={closeModal}
                title={activeTab === 'orders' ? 'Dettagli Ordine' : 'Dettagli Offerta'}
                size="2xl"
            >
                <DetailModalContent
                    detailsLoading={detailsLoading}
                    selectedItem={selectedItem}
                    itemDetails={itemDetails}
                    activeTab={activeTab}
                    closeModal={closeModal}
                />
            </Modal>
        </>
    );
};

export default OrdersStatus;